import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, Circle, ArrowRight, Loader2, AlertCircle, Sparkles,
} from "lucide-react";
import { api } from "../../lib/api";
import { LaunchPrepButton } from "../LaunchPrepButton";

/**
 * Generic readiness-coverage card.
 *
 * Reads a coverage endpoint that returns:
 *   { has_*: bool, coverage_pct: int, sections: [...], source_files_total: [...] }
 *
 * Both the financial-model and pitch-deck dashboards plug into this.
 *
 * Props:
 *   endpoint         – e.g. "/founder/readiness/pitch-deck-coverage"
 *   hasFlag          – field name in the response indicating asset present
 *                      (e.g. "has_pitch_deck" or "has_excel")
 *   title            – card title, e.g. "Pitch Deck Coverage"
 *   testId           – stable test id prefix
 *   emptyTitle       – header shown when the asset isn't uploaded yet
 *   emptyBody        – paragraph under the empty-state header
 *   uploadCtaLabel   – CTA label for the empty state ("Upload pitch deck")
 *   uploadCtaHref    – usually "/onboarding?step=2"
 *   serviceFocus     – "pitch_deck" | "financial_model" — forwarded to the
 *                      Prep sibling app (prep.clinq.africa) as the missing-doc
 *                      hint so the builder opens on the right screen.
 *   serviceCtaLabel  – CTA label when sections are missing
 *                      ("Get Clinq to build my pitch deck")
 */
export default function CoveragePanel({
  endpoint,
  hasFlag,
  title,
  testId,
  emptyTitle,
  emptyBody,
  uploadCtaLabel,
  uploadCtaHref = "/onboarding?step=2",
  serviceFocus,
  serviceCtaLabel,
}) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await api.get(endpoint);
        if (!cancelled) setData(d);
      } catch (e) {
        if (cancelled) return;
        // Sanitize: only surface short JSON `detail` strings from the
        // backend. Anything else (Cloudflare HTML 520 pages, axios
        // network-error objects, etc.) becomes a generic message so the
        // dashboard never renders raw upstream error pages inside a card.
        const raw = e?.response?.data?.detail;
        const safe =
          typeof raw === "string" && raw.length > 0 && raw.length <= 160 && !/[<>]/.test(raw)
            ? raw
            : "Couldn't load this section. Refresh to retry.";
        setErr(safe);
      }
    })();
    return () => { cancelled = true; };
  }, [endpoint]);

  if (err) {
    return (
      <div
        data-testid={testId}
        className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700"
      >
        <AlertCircle className="mr-1 inline h-4 w-4" /> {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div
        data-testid={testId}
        className="rounded-2xl border border-neutral-200 bg-white p-6"
        aria-busy="true"
      >
        <div className="h-3 w-32 animate-pulse rounded bg-neutral-100" />
        <div
          className="mt-3 h-2 w-full overflow-hidden rounded-full bg-neutral-100"
          role="progressbar"
          aria-label={`Scanning ${title.toLowerCase()}`}
          data-testid={`${testId}-loading-bar`}
        >
          <div
            className="h-2 w-1/3 rounded-full bg-gradient-to-r from-neutral-200 via-[#0A8B85]/70 to-neutral-200"
            style={{ animation: "scoreShimmer 1.4s ease-in-out infinite" }}
          />
        </div>
        <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-neutral-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Scanning {title.toLowerCase()}…
        </p>
      </div>
    );
  }

  const hasAsset = !!data[hasFlag];
  const { coverage_pct: coveragePct, sections } = data;
  const missing = (sections || []).filter((s) => !s.present);
  const missingLabels = missing.map((s) => s.label);
  // `serviceFocus` doubles as the Prep hand-off's missing-doc hint: when we
  // route into prep.clinq.africa we tell it which doc to open (pitch_deck
  // vs financial_model) so the builder lands on the right screen.
  const prepMissingDocs = serviceFocus ? [serviceFocus] : undefined;

  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-neutral-200 bg-white p-6"
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">
          {title}
        </p>
        {hasAsset && (
          <span
            data-testid={`${testId}-pct`}
            className={
              "rounded-full px-2 py-0.5 text-[11px] font-bold " +
              (coveragePct >= 70
                ? "bg-emerald-100 text-emerald-800"
                : coveragePct >= 40
                  ? "bg-amber-100 text-amber-800"
                  : "bg-red-100 text-red-800")
            }
          >
            {coveragePct}% covered
          </span>
        )}
      </div>

      {!hasAsset ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">{emptyTitle}</p>
          <p className="mt-1 text-xs leading-relaxed text-amber-900/80">{emptyBody}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link
              to={uploadCtaHref}
              data-testid={`${testId}-upload-cta`}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A8B85] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#066667]"
            >
              {uploadCtaLabel} <ArrowRight className="h-3 w-3" />
            </Link>
            <LaunchPrepButton
              prefillMissingDocs={prepMissingDocs}
              testid={`${testId}-services-cta`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#0A8B85]/30 bg-white px-3 py-1.5 text-xs font-bold text-[#066667] hover:bg-[#0A8B85]/5"
            >
              <Sparkles className="h-3 w-3" /> {serviceCtaLabel}
            </LaunchPrepButton>
          </div>
        </div>
      ) : (
        <>
          <p className="mt-2 text-xs text-neutral-600">
            We auto-detected which sections are present in your uploads.
            Anything marked complete is what we found.
          </p>
          <ul className="mt-4 space-y-2">
            {sections.map((s) => (
              <li
                key={s.key}
                data-testid={`${testId}-section-${s.key}`}
                data-present={s.present ? "1" : "0"}
                className={
                  "flex items-start gap-3 rounded-lg border p-3 " +
                  (s.present
                    ? "border-emerald-100 bg-emerald-50/40"
                    : "border-neutral-200 bg-neutral-50")
                }
              >
                {s.present ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                ) : (
                  <Circle className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" />
                )}
                <div className="min-w-0 flex-1">
                  <p
                    className={
                      "text-sm font-semibold " +
                      (s.present ? "text-emerald-900" : "text-neutral-500")
                    }
                  >
                    {s.label}
                  </p>
                  {s.present && s.source_files.length > 0 && (
                    <p className="mt-0.5 truncate text-[11px] text-neutral-500">
                      From: {s.source_files.join(", ")}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {missing.length > 0 && (
            <div className="mt-4 rounded-xl border border-[#0A8B85]/20 bg-[#F4F8F8] p-3">
              <p className="text-xs font-bold text-neutral-900">
                {missing.length} section{missing.length === 1 ? "" : "s"} missing.
              </p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-neutral-600">
                Missing sections drag your CCR Score down. Fix them either way:
              </p>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Link
                  to={uploadCtaHref}
                  data-testid={`${testId}-upload-missing`}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#0A8B85] px-3 py-2 text-center text-[11px] font-extrabold text-white hover:bg-[#066667]"
                >
                  Upload document with missing elements
                </Link>
                <LaunchPrepButton
                  prefillMissingDocs={prepMissingDocs}
                  testid={`${testId}-fix-with-clinq`}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#0A8B85]/40 bg-white px-3 py-2 text-center text-[11px] font-extrabold text-[#066667] hover:bg-[#0A8B85]/5"
                >
                  <Sparkles className="h-3 w-3" /> Build on Clinq Prep
                </LaunchPrepButton>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
